import { jsx, jsxs } from "react/jsx-runtime";
import React, { useState } from "react";
import {
  showContextMenu,
  commandMenuEnhancer,
  FillWindow
} from "@teselagen/ui";
import { CircularView } from "./CircularView";
import { LinearView } from "./LinearView";
import { RowView } from "./RowView";
import { HoveredIdContext } from "./helperComponents/withHover";
import { visibilityDefaultValues } from "./redux/annotationVisibility";
import { addWrappedAddons } from "./utils/addWrappedAddons";
import { SimpleOligoPreview } from "./SimpleOligoPreview";
import { cloneDeep, flatMap, map, startCase } from "lodash-es";
import {
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  Popover,
  Tooltip
} from "@blueprintjs/core";
import getCommands from "./commands";
import { withHandlers } from "recompose";
import { exportSequenceToFile } from "./withEditorProps";
import {
  editorClicked,
  updateSelectionOrCaret
} from "./withEditorInteractions/clickAndDragUtils";
export default (props) => {
  let {
    sequenceData: _sequenceData,
    annotationVisibility: _annotationVisibility = {},
    noWarnings = true,
    withDownload,
    withChoosePreviewType,
    withCaretEnabled,
    withSelectionEnabled,
    smallSlider,
    withVisibilityOptions,
    minimalPreviewTypeBtns,
    withFullscreen,
    selectionLayer,
    selectionLayerUpdate,
    caretPositionUpdate,
    editorName = "tg-simple-dna-view"
  } = props;
  const [previewType, setPreviewType] = useState(
    _sequenceData.circular ? "circular" : "linear"
  );
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const [isFullscreen, setFullscreen] = useState(false);
  const [visibilityOptions, setVisibilityOptions] = useState({});
  const [caretPosition, _caretPositionUpdate] = useState(
    withCaretEnabled ? -1 : void 0
  );
  const [_selectionLayer, _selectionLayerUpdate] = useState(
    withSelectionEnabled ? { start: -1, end: -1 } : void 0
  );
  const sequenceLength = _sequenceData.noSequence ? _sequenceData.size : _sequenceData.sequence.length;
  selectionLayer = selectionLayer || _selectionLayer;
  const selectionLayerUpdateOld = selectionLayerUpdate || _selectionLayerUpdate;
  const caretPositionUpdateOld = caretPositionUpdate || _caretPositionUpdate;
  selectionLayerUpdate = (newSel, dontTrigger) => {
    if (!dontTrigger && newSel.start > -1) {
      caretPositionUpdate(-1, true);
    }
    selectionLayerUpdateOld(newSel);
  };
  caretPositionUpdate = (newCaret, dontTrigger) => {
    if (!dontTrigger && newCaret > -1) {
      selectionLayerUpdate({ start: -1, end: -1 }, true);
    }
    caretPositionUpdateOld(newCaret);
  };
  function annotationClicked({ annotation, event }) {
    event.stopPropagation();
    event.preventDefault();
    withSelectionEnabled && updateSelectionOrCaret({
      doNotWrapOrigin: !sequenceData.circular,
      shiftHeld: event.shiftKey,
      sequenceLength,
      newRangeOrCaret: annotation,
      caretPosition,
      selectionLayer,
      selectionLayerUpdate,
      caretPositionUpdate
    });
  }
  let tickSpacing = void 0;
  let Component = (withChoosePreviewType ? previewType === "circular" : _sequenceData.circular) ? CircularView : _sequenceData.isOligo && _sequenceData.sequence ? SimpleOligoPreview : LinearView;
  if (withChoosePreviewType && previewType === "row") {
    Component = RowView;
    tickSpacing = void 0;
  }
  let sequenceData = cloneDeep(_sequenceData);
  const annotationVisibility = {
    ...visibilityDefaultValues,
    ..._annotationVisibility,
    ...visibilityOptions
  };
  if (sequenceData.noSequence) {
    annotationVisibility.sequence = false;
    annotationVisibility.reverseSequence = false;
    if (sequenceData.size === void 0) {
      return /* @__PURE__ */ jsxs("div", { children: [
        "Error: No sequenceData.size detected when using noSequence flag",
        " "
      ] });
    }
    sequenceData = {
      ...sequenceData,
      sequence: {
        length: sequenceData.size
      }
    };
  }
  sequenceData.parts = addWrappedAddons(
    sequenceData.parts,
    sequenceData.sequence.length
  );
  const inner = ({ width, height }) => /* @__PURE__ */ jsx(HoveredIdContext.Provider, { value: { hoveredId: props.hoveredId }, children: /* @__PURE__ */ jsxs("div", { style: { width: "fit-content" }, children: [
    (withDownload || withChoosePreviewType || withFullscreen || VisibilityOptions) && /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          marginLeft: 10,
          marginBottom: 5,
          ...isFullscreen && {
            marginRight: 10,
            paddingTop: 10
          },
          display: "flex",
          justifyContent: "end"
        },
        children: [
          withDownload && /* @__PURE__ */ jsx(DownloadBtn, { ...props }),
          withVisibilityOptions && /* @__PURE__ */ jsx(
            VisibilityOptions,
            {
              ...{
                ...props,
                sequenceData,
                annotationVisibility,
                setVisibilityOptions,
                isPopoverOpen,
                setPopoverOpen
              }
            }
          ),
          withChoosePreviewType && /* @__PURE__ */ jsxs(ButtonGroup, { children: [
            /* @__PURE__ */ jsx(Tooltip, { content: "Circular View", children: /* @__PURE__ */ jsx(
              Button,
              {
                minimal: minimalPreviewTypeBtns,
                className: "tgPreviewTypeCircular",
                active: previewType === "circular",
                intent: "primary",
                onClick: () => setPreviewType("circular"),
                icon: "circle"
              }
            ) }),
            /* @__PURE__ */ jsx(Tooltip, { content: "Linear View", children: /* @__PURE__ */ jsx(
              Button,
              {
                minimal: minimalPreviewTypeBtns,
                className: "tgPreviewTypeLinear",
                active: previewType === "linear",
                intent: "primary",
                onClick: () => setPreviewType("linear"),
                icon: "layout-linear"
              }
            ) }),
            /* @__PURE__ */ jsx(Tooltip, { content: "Sequence View", children: /* @__PURE__ */ jsx(
              Button,
              {
                minimal: minimalPreviewTypeBtns,
                className: "tgPreviewTypeRow",
                active: previewType === "row",
                intent: "primary",
                onClick: () => setPreviewType("row"),
                icon: "menu"
              }
            ) })
          ] }),
          withFullscreen && /* @__PURE__ */ jsx(
            FullscreenBtn,
            {
              ...{ setFullscreen, isFullscreen }
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      Component,
      {
        ...{
          showCicularViewInternalLabels: true,
          className: `${editorName} veEditor tg-simple-dna-view`,
          width: 300,
          height: 300,
          noWarnings,
          partClicked: annotationClicked,
          featureClicked: annotationClicked,
          primerClicked: annotationClicked,
          ...props,
          ...isFullscreen && {
            width: width - 10,
            height: height - 10
          },
          smartCircViewLabelRender: true,
          caretPosition,
          smallSlider,
          ...withSelectionEnabled && {
            selectionLayer,
            selectionLayerUpdate
          },
          readOnly: true,
          editorClicked: ({ nearestCaretPos, event } = {}) => {
            if (!withCaretEnabled) {
              if (!withSelectionEnabled) return;
              if (!event.shiftKey) return;
              if (!(selectionLayer.start > -1)) return;
            }
            editorClicked({
              nearestCaretPos,
              shiftHeld: !withSelectionEnabled ? false : event.shiftKey,
              updateSelectionOrCaret: (shiftHeld, newRangeOrCaret) => {
                updateSelectionOrCaret({
                  doNotWrapOrigin: !sequenceData.circular,
                  sequenceLength,
                  shiftHeld,
                  newRangeOrCaret,
                  caretPosition,
                  selectionLayer,
                  selectionLayerUpdate,
                  caretPositionUpdate
                });
              }
            });
          },
          instantiated: true,
          tickSpacing,
          hoveredId: props.hoveredId,
          annotationVisibility,
          sequenceData,
          showTitle: true
        }
      }
    )
  ] }) });
  if (isFullscreen) {
    return /* @__PURE__ */ jsx(FillWindow, { asPortal: true, className: "tgSimpleViewFullscreen", children: inner });
  }
  return inner({});
};
const DownloadBtn = withHandlers({ exportSequenceToFile })((props) => {
  return /* @__PURE__ */ jsx(Tooltip, { content: "Download", children: /* @__PURE__ */ jsx(
    Button,
    {
      className: "veDownloadButton",
      style: { marginRight: 10 },
      onClick: (event) => showContextMenu(
        [
          "exportSequenceAsGenbank",
          "exportDNASequenceAsFasta",
          "exportProteinSequenceAsFasta",
          "exportSequenceAsTeselagenJson"
        ],
        [
          commandMenuEnhancer(getCommands({ props }), {
            useTicks: true,
            omitIcons: true
          })
        ],
        event
      ),
      minimal: true,
      intent: "primary",
      icon: "download"
    }
  ) });
});
const FullscreenBtn = ({ setFullscreen, isFullscreen }) => {
  return /* @__PURE__ */ jsx(Tooltip, { content: `${isFullscreen ? "Close " : ""}Fullscreen`, children: /* @__PURE__ */ jsx(
    Button,
    {
      className: "veFullscreenButton",
      style: { marginLeft: 10, marginRight: 10 },
      onClick: () => {
        setFullscreen(!isFullscreen);
      },
      minimal: true,
      intent: "primary",
      icon: !isFullscreen ? "maximize" : "minimize"
    }
  ) });
};
const VisibilityOptions = ({
  annotationVisibility,
  sequenceData,
  setVisibilityOptions,
  isPopoverOpen,
  setPopoverOpen
}) => {
  return /* @__PURE__ */ jsx(Tooltip, { disabled: isPopoverOpen, content: "Visibility Options", children: /* @__PURE__ */ jsx(
    Popover,
    {
      minimal: true,
      onInteraction: (isOpen) => {
        setPopoverOpen(isOpen);
      },
      isOpen: isPopoverOpen,
      content: /* @__PURE__ */ jsx(Menu, { children: flatMap(
        ["features", "parts", "primers", "translations", "cutsites"],
        (name) => {
          if (!map(sequenceData[name]).length) return [];
          return /* @__PURE__ */ jsx(
            MenuItem,
            {
              onClick: (e) => {
                setVisibilityOptions({
                  ...annotationVisibility,
                  [name]: !annotationVisibility[name]
                });
                e.stopPropagation();
              },
              icon: annotationVisibility[name] ? "tick" : "blank",
              text: startCase(name)
            },
            name
          );
        }
      ) }),
      children: /* @__PURE__ */ jsx(
        Button,
        {
          className: "veSimpleVisibilityBtn",
          style: { marginLeft: 10, marginRight: 10 },
          minimal: true,
          intent: "primary",
          icon: "eye-open"
        }
      )
    }
  ) });
};
